<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('order_listing') }
        ]" />
        <h1>{{ $t('order_listing') }}</h1>
      </header>
      <article class="container framework-slot">
        <div class="form-row toolbar">
            <div class="col col-6 col-md-auto">

            </div>
            <div class="col d-none d-md-block"></div>
            <div class="col col-6 col-md-auto text-right">
                <button class="btn btn-outline-secondary" :title="$t('refresh_data')" :disabled="orders.loading"
                        v-on:click="refreshData">
                    <font-awesome-icon :spin="orders.loading" :icon="['fal', 'sync-alt']"/>
                </button>
            </div>
            <div v-if="stores.nodes && stores.nodes.length" class="col col-12 col-sm-6 col-md-auto">
              <input-group>
                <select class="custom-select" :aria-label="$t('store')" v-model="form.storeId"
                        v-on:change="filter.storeId = form.storeId">
                  <option v-for="store in stores.nodes" :key="store.id" :value="store.id">{{store.name}}</option>
                </select>
              </input-group>
            </div>
            <div v-if="$store.getters['thermalPrinter/configured'] && stores.nodes && stores.nodes.length" class="col col-12 col-sm-6 col-md-auto auto-printing-switch">
              <input-group>
                <div class="custom-control custom-switch">
                  <input type="checkbox" class="custom-control-input" id="auto-printing-switch" v-model="form.autoPrinting">
                  <label class="custom-control-label" for="auto-printing-switch">{{ $t('auto_printing_switch') }}</label>
                </div>
              </input-group>
            </div>

        </div>
        <order-modal v-if="showingOrder" :store="selectedStore" :order="showingOrder" @close="showingOrder = null" />
        <div v-if="orders.loading">
            <loading-state/>
        </div>
        <div v-else-if="orders.error">
            <request-error-state :try-again-handler="refreshData" :message="orders.error"/>
        </div>
        <div v-else-if="!orders.data.nodes || !orders.data.nodes.length">
            <empty-state/>
        </div>
        <infinite-scroll v-else :handler="queryMoreOrders" :should-handle="orders.data.pageInfo.hasNextPage">
            <div class="row">
                <template v-for="order in orders.data.nodes">
                    <div :key="order.id" class="col-12 col-md-6 col-lg-4">
                        <div class="result-entity">
                            <h3>{{ order.createdAt | formatDateTime }}</h3>
                            <table class="table table-bordered">
                                <tr v-if="order.sequentialNumber">
                                  <th>{{ $t("sequential_number") }}:</th>
                                  <td colspan="2" class="wrap">{{ order.sequentialNumber }}</td>
                                </tr>
                                <tr>
                                  <th>{{ $t("order_type") }}:</th>
                                  <td colspan="2" class="wrap">{{ $t(`order_type_${order.type}`) }}</td>
                                </tr>
                                <tr>
                                    <th>{{ $t("customer_name") }}:</th>
                                    <td colspan="2" class="wrap">{{ order.customerName }}</td>
                                </tr>
                                <tr v-if="order.deliveryAddress">
                                  <th>{{ $t("address_level3") }}:</th>
                                  <td colspan="2" class="wrap">{{ order.deliveryAddress.level3 }}</td>
                                </tr>
                                <tr>
                                  <th>{{ $t("items_count") }}:</th>
                                  <td colspan="2" class="wrap">{{ order.items | sumItems }}</td>
                                </tr>
                                <tr>
                                    <th>{{ $t("total") }}:</th>
                                    <td colspan="2" class="wrap">{{ order.total | formatCurrency }}</td>
                                </tr>
                            </table>
                            <div class="messages alert-danger" v-if="orders.actionErrorMap[order.id]">
                                {{ $t(orders.actionErrorMap[order.id]) }}
                            </div>
                            <div class="actions">
                              <button class="btn btn-sm btn-outline-primary" @click="showOrder(order)">
                                {{$t("show_order_button")}}
                              </button>
                              <button v-if="$store.getters['thermalPrinter/configured']" class="btn btn-sm btn-outline-primary" @click="printOrder(order)"
                                      :disabled="orders.printingMap[order.id]">
                                {{orders.printingMap[order.id] ? $t("printing") : $t("print_order_button")}}
                              </button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </infinite-scroll>
      </article>
      <order-for-printer
          v-if="printingOrder"
          ref="ofp"
          :data="{
            store: selectedStore,
            id: printingOrder.id,
            sequentialNumber: printingOrder.sequentialNumber,
            type: printingOrder.type,
            items: printingOrder.items,
            customerName: printingOrder.customerName,
            customerPhone: printingOrder.customerPhone,
            customerEmail: printingOrder.customerEmail,
            address: printingOrder.deliveryAddress,
            paymentMethod: printingOrder.paymentMethod,
            paymentMethodFee: printingOrder.paymentMethodFee,
            paymentChangeFor: printingOrder.paymentChangeFor,
            feesToBeNegotiated: printingOrder.feesToBeNegotiated,
            subTotal: printingOrder.subTotal,
            deliveryFee: printingOrder.deliveryFee,
            total: printingOrder.total,
            createdAt: printingOrder.createdAt
          }"
      />
    </app-framework>
</template>

<script>
    import AppFramework from "@/components/layout/AppFramework";
    import InfiniteScroll from "@/components/common/InfiniteScroll";
    import LoadingState from "@/components/states/LoadingState";
    import EmptyState from "@/components/states/EmptyState";
    import RequestErrorState from "@/components/states/RequestErrorState";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";
    import moment from "moment";
    import gql from "graphql-tag";
    import OrderForPrinter from "@/components/order/OrderForPrinter";
    import InputGroup from "@/components/common/InputGroup";
    import audioAlertNewOrder from "@/assets/audio/alert-new-order.mp3";
    import OrderModal from "@/components/order/OrderModal.vue";

    export default {
        name: 'orderListing',
        i18nOptions: {namespaces: ['order_listing', 'common', 'breadcrumbs']},
        components: {
          OrderModal,
          InputGroup, OrderForPrinter, BreadCrumbs, RequestErrorState, EmptyState, LoadingState, InfiniteScroll, AppFramework},
        metaInfo() {
          return {
            title: this.$t('order_listing')
          }
        },
        data: () => ({
            filter: {
              storeId: null
            },
            form: {
              storeId: null,
              autoPrinting: false
            },
            autoCheckNewOrderInterval: null,
            autoCheckLastOrderId: null,
            orders: {
                data: null,
                loading: true,
                error: null,
                banningMap: {},
                printingMap: {},
                actionErrorMap: {}
            },
            showingOrder: null,
            printingOrder: null,
            selectedStore: null,
            stores: {
              nodes: null,
              loading: true,
              error: null
            }
        }),
        watch: {
          filter: {
            deep: true,
            async handler() {
              this.selectedStore = this.stores.nodes.find(n => n.id === this.filter.storeId)
              await this.queryOrders()
              if (this.orders.data.nodes && this.orders.data.nodes.length) {
                this.autoCheckLastOrderId = this.orders.data.nodes[0].id
              }
            }
          }
        },
        async mounted() {
          await this.queryStores()
          if (!this.filter.storeId) {
            if (this.stores.nodes && this.stores.nodes.length) {
              this.filter.storeId = this.stores.nodes[0].id
              this.form.storeId = this.stores.nodes[0].id
              this.selectedStore = this.stores.nodes[0]
            }
          }
          await this.queryOrders()
          this.autoCheckNewOrder()
        },
        beforeDestroy() {
          clearInterval(this.autoCheckNewOrderInterval)
        },
        filters: {
          formatDateTime: function (date) {
            return moment(date).format("lll")
          },
          formatCurrency: function (number) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number)
          },
          sumItems: function (items) {
            return items.reduce((sum, item) => sum + item.quantity, 0)
          }
        },
        methods: {
            async queryStores(useCache = true) {
              this.stores.loading = true;
              try {
                const data = await this.$apolloClient.queryAll("stores", {
                  query: gql`query ($first: Int! $after: String) {
                      stores(first: $first after: $after) {
                          nodes {
                              id
                              name
                          }
                          pageInfo {
                              hasNextPage
                              endCursor
                          }
                      }
                  }`,
                  variables: {
                    first: 100
                  },
                  fetchPolicy: useCache ? "cache-first" : "network-only"
                });
                this.stores.nodes = data.nodes;
                this.stores.error = null;
              } catch (e) {
                if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                  if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                    this.stores.error = message;
                    return true;
                  }
                  return false;
                })) {
                  this.stores.error = "Error to load stores";
                }
              }
              this.stores.loading = false;
            },
            async queryOrders(useCache = true) {
                this.orders.printingMap = {};
                this.orders.actionErrorMap = {};
                this.orders.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($storeId: ID! $first: Int! $after: String) {
                            orders(storeId: $storeId first: $first after: $after) {
                                nodes {
                                    id
                                    sequentialNumber
                                    type
                                    customerName
                                    customerPhone
                                    customerEmail
                                    deliveryAddress {
                                        line1
                                        houseNumber
                                        line2
                                        level3
                                        instructions
                                    }
                                    items {
                                        quantity
                                        productName
                                        price
                                        choices {
                                            type
                                            title
                                            singleChoiceAnswer
                                            multipleChoiceAnswers
                                            repeatableChoiceAnswers {
                                                quantity
                                                description
                                            }
                                            textAnswer
                                        }
                                    }
                                    feesToBeNegotiated
                                    subTotal
                                    deliveryFee
                                    paymentMethod
                                    paymentMethodFee
                                    paymentChangeFor
                                    total
                                    createdAt
                                }
                                pageInfo {
                                    endCursor
                                    hasNextPage
                                }
                            }
                        }`,
                        variables: {
                            storeId: this.filter.storeId,
                            first: 21,
                        },
                        fetchPolicy: useCache ? "cache-first" : "network-only"
                    });
                    this.orders.data = {...response.data.orders};
                    this.orders.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.orders.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.orders.error = "Error to load orders";
                    }
                }
                this.orders.loading = false;
            },
            async queryMoreOrders() {
                const response = await this.$apolloClient.query({
                    query: gql`query ($storeId: ID! $first: Int! $after: String) {
                        orders(storeId: $storeId first: $first after: $after) {
                            nodes {
                                id
                                sequentialNumber
                                type
                                customerName
                                customerPhone
                                customerEmail
                                deliveryAddress {
                                    line1
                                    houseNumber
                                    line2
                                    level3
                                    instructions
                                }
                                items {
                                    quantity
                                    productName
                                    price
                                    choices {
                                        type
                                        title
                                        singleChoiceAnswer
                                        multipleChoiceAnswers
                                        repeatableChoiceAnswers {
                                            quantity
                                            description
                                        }
                                        textAnswer
                                    }
                                }
                                feesToBeNegotiated
                                subTotal
                                deliveryFee
                                paymentMethod
                                paymentMethodFee
                                paymentChangeFor
                                total
                                createdAt
                            }
                            pageInfo {
                                endCursor
                                hasNextPage
                            }
                        }
                    }`,
                    variables: {
                        storeId: this.filter.storeId,
                        first: 21,
                        after: this.orders.data.pageInfo.endCursor
                    },
                    fetchPolicy: "no-cache"
                });
                this.orders.data.nodes = [...this.orders.data.nodes, ...response.data.orders.nodes];
                this.orders.data.pageInfo = response.data.orders.pageInfo;
            },
            async refreshData() {
                await this.queryOrders(false)
                if (this.form.autoPrinting) {
                  await this.printNewOrders()
                }
            },
            async showOrder(order) {
              this.orders.actionErrorMap[order.id] = null
              this.showingOrder = order
            },
            async printOrder(order) {
              this.orders.actionErrorMap[order.id] = null
              this.orders.printingMap[order.id] = true
              this.printingOrder = order
              await this.$nextTick()
              try {
                await this.$store.dispatch('thermalPrinter/printTemplate', this.$refs.ofp.toTextRaw())
              } catch (e) {
                this.orders.actionErrorMap[order.id] = "PRINTING_ERROR"
                // error to print
              }
              this.orders.printingMap[order.id] = false
              this.printingOrder = null
            },
            async printNewOrders() {
              if (this.orders.data.nodes && this.orders.data.nodes.length) {
                const newOrders = []
                for (const order of this.orders.data.nodes) {
                  if (this.autoCheckLastOrderId === order.id) {
                    break
                  }
                  newOrders.unshift(order)
                }
                for (const newOrder of newOrders) {
                  await this.printOrder(newOrder)
                }
                this.autoCheckLastOrderId = this.orders.data.nodes[0].id
              }
            },
            autoCheckNewOrder() {
              if (this.orders.data && this.orders.data.nodes && this.orders.data.nodes.length) {
                this.autoCheckLastOrderId = this.orders.data.nodes[0].id
              } else {
                this.autoCheckLastOrderId = null
              }
              this.autoCheckNewOrderInterval = setInterval(async () => {
                try {
                  const response = await this.$apolloClient.query({
                    query: gql`query ($storeId: ID! $lastOrderId: ID) {
                        checkNewOrders(storeId: $storeId lastOrderId: $lastOrderId)
                    }`,
                    variables: {
                      storeId: this.filter.storeId,
                      lastOrderId: this.autoCheckLastOrderId
                    },
                    fetchPolicy: "network-only"
                  });
                  if (response.data.checkNewOrders) {
                    await this.queryOrders(false)
                    if (this.form.autoPrinting) {
                      await this.printNewOrders()
                    }
                    if (this.orders.data.nodes && this.orders.data.nodes.length) {
                      this.autoCheckLastOrderId = this.orders.data.nodes[0].id
                    }
                    this.alertNewOrder()
                  }
                  this.stores.error = null;
                } catch (e) {
                  // failed to check new orders
                }
              }, 60 * 1000) // 1 minute
            },
            autoPrintingChange() {
              if (this.form.autoPrinting) {
                if (this.orders.data && this.orders.data.nodes && this.orders.data.nodes.length) {
                  this.autoCheckLastOrderId = this.orders.data.nodes[0].id
                } else {
                  this.autoCheckLastOrderId = null
                }
                this.autoCheckNewOrderInterval = setInterval(async () => {
                  try {
                    const response = await this.$apolloClient.query({
                      query: gql`query ($storeId: ID! $lastOrderId: ID) {
                          checkNewOrders(storeId: $storeId lastOrderId: $lastOrderId)
                      }`,
                      variables: {
                        storeId: this.filter.storeId,
                        lastOrderId: this.autoCheckLastOrderId
                      },
                      fetchPolicy: "network-only"
                    });
                    if (response.data.checkNewOrders) {
                      await this.queryOrders(false)
                      await this.printNewOrders()
                      this.alertNewOrder()
                    }
                    this.stores.error = null;
                  } catch (e) {
                    // failed to check new orders
                  }
                }, 60 * 1000) // 1 minute
              } else {
                clearInterval(this.autoCheckNewOrderInterval)
              }
            },
            alertNewOrder() {
              const audio = new Audio(audioAlertNewOrder)
              audio.addEventListener('canplaythrough', function() {
                audio.play()
              })
            }
        }
    }
</script>

<style scoped>
  .wrap {
    overflow-wrap: anywhere;
  }
  .auto-printing-switch {
    align-items: center;
    display: flex;
  }
</style>